import { ChangeDetectionStrategy, ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { IonicModule } from '@ionic/angular';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

import { Util } from 'src/shared/util/util';
import { readSecondaryPageContext } from 'src/shared/util/secondary-page-context';

import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { FinancialConnectionsAccount, FinancialConnectionsService } from 'src/shared/services/financial-connections.service';

import { RadioButtonModel } from 'src/tidy-ui-components/components/form/radio-button/radio-button.model';
import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

interface RemoveConnectionParams {
  connection?: FinancialConnectionsAccount;
}

/** Removing a bank asks whether the P&L transactions it produced stay or go. */
@Component({
  templateUrl: 'remove-connection.page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, IonicModule, ReactiveFormsModule, TidyUiComponentsModule, SharedModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class RemoveConnectionPage implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly navCtrl = inject(CustomNavController);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly storage = inject(TidyStorage);
  private readonly util = inject(Util);
  private readonly demoBlock = inject(DemoBlockService);
  private readonly financialConnections = inject(FinancialConnectionsService);

  readonly form = this.fb.group({
    transactions: ['keep' as 'keep' | 'delete', Validators.required],
  });

  readonly transactionItems: RadioButtonModel[] = [
    { value: 'keep', viewValue: 'Keep them in my P&L' },
    { value: 'delete', viewValue: 'Delete them from my P&L' },
  ];

  isRightSideContent = true;
  loaded = false;
  submitted = false;
  saving = false;
  errorMessage = '';
  connection: FinancialConnectionsAccount | null = null;
  connectionLabel = '';

  readonly boundRemove = () => this.remove();
  readonly boundCancel = () => this.goBack();

  async ngOnInit() {
    const { isRightSideContent, params } = await readSecondaryPageContext<RemoveConnectionParams>(this.storage);
    this.isRightSideContent = isRightSideContent;
    this.connection = params.connection ?? null;
    this.rightSidePanelService.setDialogPageTitle('Remove Bank Account');
    if (this.connection) {
      this.connectionLabel = `${this.connection.institution_name} ····${this.connection.last4}`;
    } else {
      this.errorMessage = 'Bank account not found.';
    }
    this.loaded = true;
    this.cdr.markForCheck();
  }

  async remove(): Promise<void> {
    this.submitted = true;
    if (this.form.invalid || this.saving || !this.connection) { return; }
    if (this.demoBlock.block()) { return; }
    this.saving = true;
    this.errorMessage = '';
    this.cdr.markForCheck();
    try {
      await this.financialConnections.disconnectAccount(this.connection.id, this.form.controls.transactions.value === 'delete');
      await this.storage.save('didMakeConnectionChanges', true);
      this.util.showSuccess(`${this.connectionLabel} removed`);
      this.goBack();
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Unable to remove the bank account.';
    } finally {
      this.saving = false;
      this.cdr.markForCheck();
    }
  }

  goBack(): void {
    if (this.isRightSideContent) {
      if (this.rightSidePanelService.historyContext$.getValue().length > 1) {
        this.rightSidePanelService.goBack();
      } else {
        this.rightSidePanelService.closeRightSidePanel();
      }
    } else {
      this.navCtrl.back();
    }
  }
}
